import { useMemo, useState, type ReactNode } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useI18n } from '@/i18n'
import { verifyPin } from '@/auth/pin/PinVerifier'
import {
  NO_ATTEMPTS,
  isLockedOut,
  recordFailure,
  recordSuccess,
  triesLeft,
} from '@/auth/pin/lockout'
import { hasAnyRole, ROLES } from '@/auth/roles'
import { getMeta, roster, setMeta, type TeamMember } from '@/offline'
import { Banner, ChoiceGroup, PinPad, Sheet } from '@/ui'

/** The supervisor picks his name and enters his own PIN; checked against the roster hash. */
export function SupervisorPinSheet({
  reason,
  onDone,
}: {
  reason: ReactNode
  onDone: (m: TeamMember | null) => void
}) {
  const { t } = useI18n()
  const team = useLiveQuery(() => roster.team(), [])
  const supervisors = useMemo(
    () => (team ?? []).filter((m) => hasAnyRole(m.roles, [ROLES.SUPERVISOR]) && m.pin_hash),
    [team],
  )
  const [picked, setPicked] = useState<string | null>(null)
  const selectedId = picked ?? (supervisors.length === 1 ? supervisors[0]!.user_id : null)
  const selected = supervisors.find((s) => s.user_id === selectedId) ?? null
  const [error, setError] = useState<string | null>(null)
  const [signal, setSignal] = useState(0)
  const [busy, setBusy] = useState(false)

  const onPin = async (pin: string) => {
    if (!selected?.pin_hash) return
    setBusy(true)
    const now = Date.now()
    const attempts = (await getMeta('pin_attempts')) ?? NO_ATTEMPTS
    if (isLockedOut(attempts, now)) {
      setBusy(false)
      setError(t('pin.lockedOut', { seconds: Math.ceil((attempts.locked_until - now) / 1000) }))
      setSignal((n) => n + 1)
      return
    }
    const ok = await verifyPin(pin, selected.pin_hash)
    setBusy(false)
    if (ok) {
      await setMeta('pin_attempts', recordSuccess())
      const { pin_hash: _h, ...safe } = selected
      onDone(safe as TeamMember)
      return
    }
    const next = recordFailure(attempts, Date.now())
    await setMeta('pin_attempts', next)
    setSignal((n) => n + 1)
    setError(
      isLockedOut(next, Date.now())
        ? t('pin.lockedOut', { seconds: Math.ceil((next.locked_until - Date.now()) / 1000) })
        : t('pin.wrong', { left: triesLeft(next) }),
    )
  }

  return (
    <Sheet open onClose={() => onDone(null)} title={t('gate.sup.title')} description={reason}>
      <div className="flex flex-col gap-4">
        {supervisors.length === 0 ? (
          <Banner tone="warn">{t('gate.sup.none')}</Banner>
        ) : (
          <>
            {supervisors.length > 1 && (
              <ChoiceGroup
                layout="stack"
                label={t('gate.sup.who')}
                options={supervisors.map((s) => ({ value: s.user_id, label: s.name }))}
                value={selectedId}
                onChange={setPicked}
              />
            )}
            {selected && (
              <>
                <p className="text-center text-[15px] font-semibold">{selected.name}</p>
                {error && <Banner tone="error">{error}</Banner>}
                <PinPad
                  onComplete={(p) => void onPin(p)}
                  errorSignal={signal}
                  disabled={busy}
                  label={t('pin.enter')}
                />
              </>
            )}
          </>
        )}
      </div>
    </Sheet>
  )
}
